import { addDays, max, min, startOfDay, subDays, isFuture } from 'date-fns';
import { toDate, zonedTimeToUtc, formatInTimeZone } from 'date-fns-tz';
import { stateToTzMap } from '@ourbranch/state-to-tz-map';
import { policyTypes } from '@ourbranch/policy-types';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { STAFF_AUTO_MAX_BACKDATE_DAYS } from '@ourbranch/policy-utils';
import { appConfig } from 'brand-config';

// Re-exported from @ourbranch/policy-utils so the date picker, the FE submission gate
// (isAutoBackdateChangeAllowed), and the BE backstop all read a single source of truth and
// can't desync. Keep importing STAFF_AUTO_MAX_BACKDATE_DAYS from here in staff-fe.
export { STAFF_AUTO_MAX_BACKDATE_DAYS };
const STAFF_AUTO_MAX_FORWARD_DAYS = 60;

const isAutoPolicyType = (policyType) => policyType?.includes(policyTypes.Auto);

/**
 * @deprecated since 2021-09-01
 * consider using `localToUtcTime` if you need to convert an iso string (eg: 2022-09-01) to a date object for comparison
 * */
export const dateFromPolicy = (date = new Date()) => {
  return toDate(date);
};

/**
 *
 * @param {*} date ISO string date like '2021-04-16'
 * @param {*} state state the policy is in
 */
export const localToUtcTime = (date, state) => {
  return zonedTimeToUtc(date, stateToTzMap[state]);
};

export const getTimezone = (state) => {
  if (['WA', 'CA', 'OR', 'NV'].includes(state)) {
    return 'PT';
  }
  if (['AZ', 'NM', 'WY', 'UT', 'CO', 'MT', 'ID', 'ND', 'SD', 'NE'].includes(state)) {
    return 'MT';
  }
  if (['AL', 'AR', 'IL', 'IA', 'LA', 'MN', 'MO', 'MS', 'OK', 'WI', 'IN', 'KS', 'MI', 'TN', 'TX'].includes(state)) {
    return 'CT';
  }

  if (state === 'HI') {
    return 'HT';
  }
  if (state === 'AK') {
    return 'AKT';
  }
  // default to eastern
  return 'ET';
};

export const convertUTCToET = (date) => formatInTimeZone(date, 'America/New_York', 'MM/dd/yyyy @ h:mm a');

export function getStartMinMaxDate({
  policy,
  editCurrentSegment = false,
  currentSegment = undefined,
  canBackDate = false,
  editingPolicySettings = false,
  policyType = undefined,
  isAgency = false
}) {
  const currentSegmentIndex = policy?.segments?.findIndex((segment) => segment.segmentId === currentSegment.segmentId);
  const isFirstSegment = currentSegmentIndex === 0;
  const isMostRecentSegment = currentSegmentIndex === policy?.segments?.length - 1;
  let startDate = toDate(currentSegment.startDate);
  const effectiveDate = toDate(policy.effectiveDate);
  let maxDate = toDate(policy.endDate);
  let minDate = new Date();
  const today = new Date();

  // editing effective date on policy settings tab
  if (editingPolicySettings) {
    // Forreston Farm agent endorsement rules also apply on the policy settings tab (GitHub issue #29728)
    const settingsEndorsementRules =
      isFarmBasedPolicyType(policyType) && isAgency && appConfig?.effectiveDateRules?.agent?.endorsement;
    if (settingsEndorsementRules) {
      return {
        startDate,
        minDate: addDays(new Date(), -settingsEndorsementRules.maxBackdateDays),
        maxDate: addDays(new Date(), settingsEndorsementRules.maxForwardDays)
      };
    }
    if (isFuture(effectiveDate)) {
      return {
        startDate,
        minDate,
        maxDate: addDays(minDate, 60)
      };
    }
    return {
      startDate,
      minDate: isFuture(startDate) ? minDate : startDate,
      maxDate: addDays(startDate, 60)
    };
  }

  // editing segments on details tab
  if (canBackDate) {
    if (editCurrentSegment && currentSegment) {
      if (isFirstSegment) {
        // allow users who can back date to move policy effective date to ~2 months out from today if future effective, effective date if past the effective day
        minDate = isFuture(effectiveDate) ? today : startDate;
        maxDate = addDays(isFuture(effectiveDate) ? today : startDate, 60);
      } else {
        minDate = addDays(toDate(policy?.segments[currentSegmentIndex - 1]?.startDate), 1);
      }
      if (!isMostRecentSegment && !isFirstSegment) {
        maxDate = subDays(toDate(policy?.segments[currentSegmentIndex + 1]?.startDate), 1);
      }
    } else {
      // if creating a new segment, min date should be one day after the policy effective date
      // and start date should be tomorrow
      minDate = addDays(toDate(policy.effectiveDate), 1);
      startDate = addDays(new Date(), 1);
    }
  } else if (isAutoPolicyType(policyType)) {
    // Auto policy: staff users can backdate auto endorsements up to 30 days. Disallowed change
    // types (anything beyond add car / remove car / add driver) are caught by the submission-time
    // validator in details.jsx (`isAutoBackdateChangeAllowed`), so the date picker stays flexible
    // here. (GitHub issue #30442)
    //
    // Floor at the policy effective date and cap at the policy end date so the picker can't offer a
    // startDate outside the policy term — the BE's segment-validation-rules would reject those after
    // a round-trip, surfacing as a confusing post-submit error. startOfDay anchors the 30-day floor
    // to midnight so the boundary is exactly 30 calendar days regardless of the current wall-clock time
    // (segment startDates are date-only and parse to midnight). `effectiveDate`/`maxDate` reuse the
    // locals parsed above rather than re-parsing the raw policy strings.
    minDate = max([addDays(effectiveDate, 1), startOfDay(addDays(new Date(), -STAFF_AUTO_MAX_BACKDATE_DAYS))]);
    maxDate = min([maxDate, addDays(new Date(), STAFF_AUTO_MAX_FORWARD_DAYS)]);
    if (editCurrentSegment && currentSegment) {
      if (isFirstSegment) {
        minDate = startDate;
        maxDate = minDate;
      } else {
        // Constrain by the previous segment's startDate regardless of whether this segment is past or future —
        // edits within the 30-day backdate window must still respect segment ordering to avoid overlapping/invalid timelines.
        minDate = max([addDays(toDate(policy?.segments[currentSegmentIndex - 1]?.startDate), 1), minDate]);
      }
      if (!isMostRecentSegment && !isFirstSegment) {
        maxDate = subDays(toDate(policy?.segments[currentSegmentIndex + 1]?.startDate), 1);
      }
    } else {
      startDate = addDays(new Date(), 1);
    }
    // Guard against an inverted/empty range: for a future-effective policy (effectiveDate beyond the
    // forward window) or an already-expired term (endDate in the past), the floor can exceed the cap.
    // Collapse to a single valid day and keep the default startDate inside [minDate, maxDate] so the
    // picker never opens on an out-of-range value.
    if (minDate > maxDate) {
      maxDate = minDate;
    }
    startDate = max([minDate, min([startDate, maxDate])]);
  } else {
    // Forreston Farm agent endorsement: allow backdating up to configured days (GitHub issue #29728)
    const endorsementRules =
      isFarmBasedPolicyType(policyType) && isAgency && appConfig?.effectiveDateRules?.agent?.endorsement;
    if (endorsementRules) {
      minDate = addDays(new Date(), -endorsementRules.maxBackdateDays);
      maxDate = addDays(new Date(), endorsementRules.maxForwardDays);
      if (editCurrentSegment && currentSegment) {
        if (isFirstSegment) {
          minDate = startDate;
          maxDate = minDate;
        } else if (!isFirstSegment && isFuture(startDate)) {
          minDate = max([addDays(toDate(policy?.segments[currentSegmentIndex - 1]?.startDate), 1), minDate]);
        }
        if (!isMostRecentSegment && !isFirstSegment) {
          maxDate = subDays(toDate(policy?.segments[currentSegmentIndex + 1]?.startDate), 1);
        }
      } else {
        startDate = addDays(new Date(), 1);
      }
    } else {
      // if a user can't back date, default minDate will be the later of one day after the first segment or tomorrow.
      minDate = max([addDays(toDate(policy.effectiveDate), 1), addDays(new Date(), 1)]);
      // a user without back dating permission will still be able to move dates of segments that start in the future (excluding the first)
      if (editCurrentSegment && currentSegment) {
        if (isFirstSegment) {
          minDate = startDate;
          maxDate = minDate;
        } else if (!isFirstSegment && isFuture(startDate)) {
          // if editing a segment that starts in the future, allow date to be changed to the later of one day after the preceding segment or today
          minDate = max([addDays(toDate(policy?.segments[currentSegmentIndex - 1]?.startDate), 1), new Date()]);
        }
        if (!isMostRecentSegment && !isFirstSegment) {
          maxDate = subDays(toDate(policy?.segments[currentSegmentIndex + 1]?.startDate), 1);
        }
      } else {
        startDate = minDate;
      }
    }
  }

  return { startDate, minDate, maxDate };
}
